import type { QueryClient } from '@tanstack/react-query';
import { rebaseMediaUrls } from '@/api/mediaUrl';
import type { ConversationSummary, MessageSummary } from '@/api/types';
import { getConfig } from '@/config/config';
import { messagingKeys, type ChatMessage } from '@/features/messaging/queries';
import { useChatPresenceStore } from '@/state/chatPresenceStore';
import { useSessionStore } from '@/state/sessionStore';
import { registerBinding } from './bindings';
import { tabs } from './tabChannel';

/**
 * Live messaging (mobile messagingStore's socket listeners): server events
 * write straight into the query cache, so every open list and chat updates.
 */
const isString = (value: unknown): value is string => typeof value === 'string';

function isMessage(value: unknown): value is MessageSummary {
  const m = value as Record<string, unknown> | null;
  return (
    !!m &&
    isString(m.id) &&
    isString(m.conversationId) &&
    isString(m.senderId) &&
    isString(m.contentType) &&
    isString(m.content) &&
    isString(m.sentAt)
  );
}

/** Adds or replaces a message by id, keeping the list in time order. */
export function upsertMessage(
  list: ChatMessage[] | undefined,
  message: ChatMessage,
): ChatMessage[] {
  const current = list ?? [];
  if (current.some((m) => m.id === message.id)) {
    return current.map((m) => (m.id === message.id ? { ...m, ...message } : m));
  }
  return [...current, message];
}

/**
 * The server echoes my own message to every tab, sometimes before this tab's
 * send is acknowledged: the echo takes the place of the oldest matching
 * pending copy, so the message never shows twice.
 */
function withoutPendingCopy(list: ChatMessage[] | undefined, echo: MessageSummary) {
  if (!list || echo.senderId !== useSessionStore.getState().member?.id) return list;
  const pending = list.find(
    (m) =>
      m.localStatus === 'sending' &&
      m.contentType === echo.contentType &&
      m.content === echo.content,
  );
  return pending ? list.filter((m) => m !== pending) : list;
}

function patchMessage(
  queryClient: QueryClient,
  messageId: string,
  patch: (message: ChatMessage) => ChatMessage,
  conversationId?: string,
) {
  queryClient.setQueriesData<ChatMessage[]>(
    { queryKey: conversationId ? messagingKeys.messages(conversationId) : ['messages'] },
    (list) =>
      list?.some((m) => m.id === messageId)
        ? list.map((m) => (m.id === messageId ? patch(m) : m))
        : list,
  );
}

function bumpConversation(queryClient: QueryClient, message: MessageSummary) {
  const key = messagingKeys.conversations(false);
  const list = queryClient.getQueryData<ConversationSummary[]>(key);
  const known = list?.find((c) => c.id === message.conversationId);
  if (!list || !known) {
    // A new conversation, or one coming back from the archive.
    void queryClient.invalidateQueries({ queryKey: messagingKeys.allConversations });
    return;
  }
  // The list may already have been refetched with this message (the socket can be slower than
  // HTTP): an event for the latest message or an older one changes nothing.
  const last = known.lastMessage;
  if (last && (last.id === message.id || new Date(last.sentAt) > new Date(message.sentAt))) {
    return;
  }
  const myId = useSessionStore.getState().member?.id;
  const reading = useChatPresenceStore.getState().openConversationId === message.conversationId;
  // The community group never has unread messages (the server counts 0 for it, as on Android).
  const counts = known.type !== 'group' && message.senderId !== myId && !reading;
  const updated: ConversationSummary = {
    ...known,
    unreadCount: (known.unreadCount ?? 0) + (counts ? 1 : 0),
    lastMessage: {
      id: message.id,
      senderId: message.senderId,
      contentType: message.contentType,
      content: message.content,
      sentAt: message.sentAt,
    },
  };
  queryClient.setQueryData<ConversationSummary[]>(key, [
    updated,
    ...list.filter((c) => c.id !== message.conversationId),
  ]);
}

registerBinding('messaging', 'message:new', (raw, { queryClient }) => {
  if (!isMessage(raw)) return;
  // Uploads are stored with the address the sender used: point them at this app's API.
  const payload = rebaseMediaUrls(raw, getConfig().apiUrl);
  const key = messagingKeys.messages(payload.conversationId);
  if (queryClient.getQueryData(key)) {
    queryClient.setQueryData<ChatMessage[]>(key, (list) =>
      upsertMessage(withoutPendingCopy(list, payload), payload),
    );
  } else if (queryClient.getQueryState(key)) {
    // The history is still loading and may have been read before this message was stored.
    void queryClient.invalidateQueries({ queryKey: key });
  }
  if (
    payload.conversationId !==
    queryClient.getQueryData<ConversationSummary>(messagingKeys.communityGroup)?.id
  ) {
    bumpConversation(queryClient, payload);
  }
});

registerBinding('messaging', 'message:read-receipt', (payload, { queryClient }) => {
  const p = payload as Record<string, unknown> | null;
  if (!p || !isString(p.conversationId) || !isString(p.messageId)) return;
  const readAt = new Date().toISOString();
  patchMessage(
    queryClient,
    p.messageId,
    (m) => ({ ...m, readAt: m.readAt ?? readAt }),
    p.conversationId,
  );
});

registerBinding('messaging', 'message:reaction-added', (payload, { queryClient }) => {
  const p = payload as Record<string, unknown> | null;
  if (!p || !isString(p.messageId) || !isString(p.memberId) || !isString(p.reactionType)) return;
  const { memberId, reactionType } = p;
  patchMessage(queryClient, p.messageId, (m) => ({
    ...m,
    // One reaction per member per message, as the backend keeps it.
    reactions: [
      ...(m.reactions ?? []).filter((r) => r.memberId !== memberId),
      { memberId, reactionType },
    ],
  }));
});

/** Marks a conversation read in this tab's list (the badge and the tab title follow). */
export function clearUnread(queryClient: QueryClient, conversationId: string) {
  queryClient.setQueryData<ConversationSummary[]>(messagingKeys.conversations(false), (list) =>
    list?.map((c) => (c.id === conversationId && c.unreadCount ? { ...c, unreadCount: 0 } : c)),
  );
}

/** Another tab read a conversation: clear it here too (data-model.md B3, WEB-011). */
export function startMessagingTabSync(queryClient: QueryClient): () => void {
  return tabs.subscribe((message) => {
    if (message.type === 'messages:read') clearUnread(queryClient, message.conversationId);
  });
}
